<template>
  <section id="demo" class="py-24 overflow-hidden bg-white">
    <div class="max-w-7xl mx-auto px-6 space-y-32">
      <!-- Item 3: Floating CTA & Support (懸浮按鈕) -->
      <div class="flex flex-col md:flex-row items-center gap-12 reveal">
        <!-- Image / UI Mockup (Left Side) -->
        <div class="w-full md:w-1/2 relative group">
          <!-- 背景裝飾 -->
          <div
            class="absolute inset-0 bg-gradient-to-tr from-gray-100 to-rose-50 rounded-3xl -rotate-2 scale-95 -z-10 transition-transform group-hover:rotate-0"
          ></div>

          <!-- Phone Screen Mockup -->
          <div
            class="bg-gray-50 border border-gray-200 rounded-3xl shadow-2xl overflow-hidden h-[500px] relative mx-auto max-w-[320px]"
          >
            <!-- 模擬網頁內容 (模糊背景) -->
            <div class="p-4 space-y-4 opacity-30 blur-[1px]">
              <div class="h-40 bg-gray-200 rounded-lg w-full"></div>
              <div class="space-y-2">
                <div class="h-4 bg-gray-200 rounded w-3/4"></div>
                <div class="h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
              <div class="h-40 bg-gray-200 rounded-lg w-full"></div>
              <div class="space-y-2">
                <div class="h-4 bg-gray-200 rounded w-3/4"></div>
                <div class="h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
            </div>

            <!-- ★★★ 懸浮按鈕群組 (重點展示) ★★★ -->
            <div
              class="absolute bottom-6 right-4 flex flex-col items-end gap-3 z-10"
            >
              <!-- 1. 線上客服按鈕 -->
              <div
                class="flex items-center gap-3 animate-float"
                style="animation-duration: 4s"
              >
                <!-- Tooltip -->
                <div
                  class="bg-white text-black text-sm font-bold px-3 py-1.5 rounded shadow-md border border-gray-100"
                >
                  線上客服
                </div>
                <!-- Icon Button -->
                <div
                  class="w-14 h-14 bg-[#991b1b] rounded-full text-white flex items-center justify-center shadow-lg hover:scale-110 transition-transform cursor-pointer"
                >
                  <svg class="w-7 h-7" fill="currentColor" viewBox="0 0 24 24">
                    <path
                      d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"
                    ></path>
                  </svg>
                </div>
              </div>

              <!-- 2. 立即選購按鈕 -->
              <div
                class="bg-black text-white px-6 py-3 rounded-full font-bold text-base shadow-xl flex items-center gap-2 cursor-pointer hover:bg-gray-800 transition-colors animate-float"
                style="animation-duration: 5s"
              >
                立即選購
                <svg
                  class="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 14l-7 7m0 0l-7-7m7 7V3"
                  ></path>
                </svg>
              </div>
            </div>
          </div>
        </div>

        <!-- Text Content (Right Side) -->
        <div class="w-full md:w-1/2 md:pl-10">
          <span
            class="text-brand font-bold tracking-widest text-xs uppercase mb-2 block"
            >Always on Call</span
          >
          <h3 class="text-3xl font-bold mb-4">
            隨時待命的購買引導，<br />不錯過任何轉化機會。
          </h3>
          <p class="text-gray-500 leading-relaxed mb-6">
            我們深知手機版面的空間限制。透過智慧懸浮按鈕設計，將最重要的「購買」與「客服」功能固定於拇指熱區。消費者無論瀏覽到頁面何處，都能一鍵下單或尋求協助，有效降低跳出率。
          </p>
          <ul class="space-y-4 text-sm font-medium text-gray-700">
            <li class="flex items-start gap-3">
              <div
                class="w-8 h-8 rounded-full bg-black text-white flex items-center justify-center shrink-0 mt-1"
              >
                <svg
                  class="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                  ></path>
                </svg>
              </div>
              <div>
                <span class="font-bold text-black block text-base"
                  >立即選購</span
                >
                點擊後平滑捲動至商品區，縮短猶豫時間。
              </div>
            </li>
            <li class="flex items-start gap-3">
              <div
                class="w-8 h-8 rounded-full bg-[#991b1b] text-white flex items-center justify-center shrink-0 mt-1"
              >
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path
                    d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"
                  ></path>
                </svg>
              </div>
              <div>
                <span class="font-bold text-black block text-base"
                  >線上客服</span
                >
                直接串接 LINE 或 Messenger，即時解決顧客疑問。
              </div>
            </li>
          </ul>
        </div>
      </div>

      <!-- Item 1: Promotion System (優惠系統) -->
      <div class="flex flex-col md:flex-row items-center gap-12 reveal">
        <!-- Image / UI Mockup -->
        <div class="w-full md:w-1/2 relative group">
          <!-- 背景裝飾 -->
          <div
            class="absolute inset-0 bg-gradient-to-br from-green-50 to-emerald-50 rounded-3xl -rotate-2 scale-95 -z-10 transition-transform group-hover:rotate-0"
          ></div>

          <!-- UI Card -->
          <div
            class="bg-white p-6 rounded-2xl shadow-xl border border-gray-100 space-y-4"
          >
            <div class="flex items-center gap-2 mb-2">
              <svg
                class="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                ></path>
              </svg>
              <h3 class="font-bold text-gray-900">優惠資訊</h3>
            </div>

            <!-- Promo 1: Discount (Achieved) -->
            <div
              class="bg-emerald-50 border border-emerald-100 rounded-xl p-4 relative overflow-hidden"
            >
              <div class="flex justify-between items-center mb-2 relative z-10">
                <div
                  class="flex items-center gap-2 text-sm font-bold text-emerald-800"
                >
                  <span>✓</span>
                  <span>母親節特惠 | 保養品全系列 95 折！</span>
                  <span
                    class="bg-white text-emerald-600 px-2 py-0.5 rounded text-xs border border-emerald-200"
                    >打 9.5 折</span
                  >
                </div>
              </div>
              <div
                class="w-full bg-emerald-200 h-1.5 rounded-full overflow-hidden"
              >
                <div class="h-full bg-emerald-500 w-full"></div>
              </div>
            </div>

            <!-- Promo 2: Cash Off (Progress) -->
            <div
              class="bg-white border border-gray-100 rounded-xl p-4 shadow-sm"
            >
              <div
                class="flex flex-wrap items-center gap-2 text-sm text-gray-600 mb-2"
              >
                <span class="font-bold text-pink-500">滿額折抵</span>
                <span>滿 $10,000 折 $1,000</span>
                <span class="text-xs text-gray-400">|</span>
                <span
                  >再買
                  <span class="text-black font-bold">$4,129</span>
                  可折抵</span
                >
              </div>
              <div
                class="w-full bg-gray-100 h-1.5 rounded-full overflow-hidden"
              >
                <div class="h-full bg-gray-800 w-[60%]"></div>
              </div>
            </div>

            <!-- Promo 3: Free Shipping (Progress) -->
            <div
              class="bg-white border border-gray-100 rounded-xl p-4 shadow-sm"
            >
              <div
                class="flex flex-wrap items-center gap-2 text-sm text-gray-600 mb-2"
              >
                <span class="font-bold text-gray-900">家電商品滿三萬免運</span>
                <span
                  >再買
                  <span class="text-black font-bold">$24,129</span>
                  享免運</span
                >
              </div>
              <div
                class="w-full bg-gray-100 h-1.5 rounded-full overflow-hidden"
              >
                <div class="h-full bg-black w-[20%]"></div>
              </div>
            </div>

            <!-- Promo 4: Gift (Progress) -->
            <div
              class="bg-white border border-gray-100 rounded-xl p-4 shadow-sm"
            >
              <div
                class="flex flex-wrap items-center gap-2 text-sm text-gray-600 mb-2"
              >
                <span class="font-bold text-gray-900">周年慶活動贈品</span>
                <span
                  >再買
                  <span class="text-black font-bold">$1,129</span> 享滿額贈
                  <span class="underline">測試小禮品</span></span
                >
              </div>
              <div
                class="w-full bg-gray-100 h-1.5 rounded-full overflow-hidden"
              >
                <div class="h-full bg-black w-[80%]"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Text Content -->
        <div class="w-full md:w-1/2 md:pl-10">
          <span
            class="text-brand font-bold tracking-widest text-xs uppercase mb-2 block"
            >Powerful Promotion Engine</span
          >
          <h3 class="text-3xl font-bold mb-4">
            靈活多變的優惠系統，<br />刺激客單價成長。
          </h3>
          <p class="text-gray-500 leading-relaxed mb-6">
            不再受限於單一的折扣模式。我們的系統支援多種促銷組合，並透過視覺化的進度條，主動提示消費者「再買多少」即可享有優惠，有效提升結帳金額。
          </p>
          <ul class="space-y-3 text-sm font-medium text-gray-700">
            <li class="flex items-center gap-3">
              <span class="w-1.5 h-1.5 rounded-full bg-brand"></span>
              支援
              <span class="font-bold text-black"
                >全館打折 / 現金折抵 / 組合價 / 滿額免運 / 滿額贈品</span
              >
            </li>
            <li class="flex items-center gap-3">
              <span class="w-1.5 h-1.5 rounded-full bg-brand"></span>
              設定
              <span class="font-bold text-black"
                >金額門檻 / 套用分類 / 日期區間</span
              >
            </li>
          </ul>
        </div>
      </div>

      <!-- Item 2: Checkout Cart (結帳功能) -->
      <div class="flex flex-col md:flex-row-reverse items-center gap-12 reveal">
        <!-- Image / UI Mockup -->
        <div class="w-full md:w-1/2 relative group">
          <!-- 背景裝飾 -->
          <div
            class="absolute inset-0 bg-gradient-to-bl from-gray-100 to-white rounded-3xl rotate-2 scale-95 -z-10 transition-transform group-hover:rotate-0"
          ></div>

          <!-- UI Card -->
          <div
            class="bg-white p-6 rounded-2xl shadow-xl border border-gray-100 max-w-sm mx-auto"
          >
            <div class="flex justify-between items-center mb-6">
              <h3 class="text-lg font-bold flex items-center gap-2">
                <svg
                  class="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                  ></path>
                </svg>
                您的購物清單
              </h3>
              <span class="text-xs text-gray-500">共 1 件商品</span>
            </div>

            <!-- Cart Item -->
            <div
              class="border border-gray-100 rounded-xl p-4 mb-6 relative hover:border-gray-300 transition-colors"
            >
              <button
                class="absolute top-3 right-3 text-gray-300 hover:text-red-500"
              >
                <svg
                  class="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                  ></path>
                </svg>
              </button>
              <div class="flex gap-4">
                <div
                  class="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden shrink-0"
                >
                  <!-- Placeholder for Product Image -->
                  <svg
                    class="w-8 h-8 text-gray-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    ></path>
                  </svg>
                </div>
                <div class="flex-1 min-w-0">
                  <h4 class="font-bold text-gray-900 text-sm mb-1 truncate">
                    一個超棒的商品
                  </h4>
                  <p class="text-xs text-gray-500 mb-2">單一規格</p>
                  <span
                    class="inline-block bg-rose-50 text-rose-600 text-[10px] font-bold px-1.5 py-0.5 rounded border border-rose-100"
                    >打9.5折</span
                  >
                </div>
              </div>
              <div class="flex justify-between items-end mt-3">
                <div
                  class="flex items-center border border-gray-200 rounded-md"
                >
                  <button
                    class="px-2 py-1 text-gray-400 hover:text-black text-sm"
                  >
                    -
                  </button>
                  <span class="px-2 text-sm font-medium">1</span>
                  <button
                    class="px-2 py-1 text-gray-400 hover:text-black text-sm"
                  >
                    +
                  </button>
                </div>
                <div class="text-right">
                  <span class="text-xs text-gray-400 line-through mr-1"
                    >$6,180</span
                  >
                  <span class="text-lg font-bold text-rose-600">$5,871</span>
                </div>
              </div>
            </div>

            <!-- Coupon Input -->
            <div
              class="flex items-center gap-2 border-t border-gray-100 pt-4 mb-4 text-sm text-gray-500 cursor-pointer hover:text-black"
            >
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                ></path>
              </svg>
              <span>使用優惠代碼</span>
            </div>

            <!-- Summary -->
            <div class="text-center mb-6">
              <p class="text-xs text-gray-500 mb-1">
                訂單小計 (不含運費、手續費)
              </p>
              <p class="text-3xl font-bold text-gray-900">$5,871</p>
            </div>

            <!-- CTA Button -->
            <button
              class="w-full bg-black text-white py-4 rounded-xl font-bold text-lg hover:bg-gray-800 transition-transform active:scale-95 flex items-center justify-center gap-2 shadow-lg"
            >
              前往結帳
              <svg
                class="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M14 5l7 7m0 0l-7 7m7-7H3"
                ></path>
              </svg>
            </button>
          </div>
        </div>

        <!-- Text Content -->
        <div class="w-full md:w-1/2 md:pr-10">
          <span
            class="text-brand font-bold tracking-widest text-xs uppercase mb-2 block"
            >Smart Checkout</span
          >
          <h3 class="text-3xl font-bold mb-4">
            流暢的結帳體驗，<br />編輯與折扣一次搞定。
          </h3>
          <p class="text-gray-500 leading-relaxed mb-6">
            我們優化了購物車的每一個細節。使用者可以在結帳頁面直接調整商品數量或移除品項，並即時查看折扣後的金額。支援一鍵套用優惠代碼，減少跳轉，讓結帳過程如絲般順滑。
          </p>
          <div class="grid grid-cols-2 gap-4">
            <div class="bg-gray-50 p-4 rounded-xl">
              <h4 class="font-bold text-sm mb-1">即時編輯</h4>
              <p class="text-xs text-gray-500">不需離開頁面即可增減數量。</p>
            </div>
            <div class="bg-gray-50 p-4 rounded-xl">
              <h4 class="font-bold text-sm mb-1">折扣明細</h4>
              <p class="text-xs text-gray-500">清楚標示原價與折扣後價格。</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: "ShowCaseSection",
};
</script>
